import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import clsx from 'clsx';
import get from 'lodash/get';
import { isEmpty, orderBy, startCase } from 'lodash';
import { Route, Switch, useRouteMatch, useParams, useLocation, useHistory } from 'react-router-dom';
import { Pivot, PivotItem, PivotLinkSize } from 'office-ui-fabric-react/lib/Pivot';
import { MessageBar, MessageBarType, DialogType } from 'office-ui-fabric-react';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { FontIcon } from 'office-ui-fabric-react/lib/Icon';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { useStatementRejection } from '../../context/statement-rejection';
import DimensionListOverview from '../DimensionListOverview';
import DimensionListDetails from '../DimensionListDetails';
import {
  fetchDimensionListOverview,
  approveDimensionListEdits,
  rejectDimensionListEdits,
  fetchDimensionListChangelog,
} from '../../actions/dimensionList';
import { showFlashMessage } from '../../actions/ui';
import PageTitle from '../../components/PageTitle';
import Main from '../../components/Main';
import DimensionListActionRequired from '../../components/DimensionListActionRequired';
import DimensionListReviewerDialog from '../../components/DimensionListReviewerDialog';
import saveExcel from '../../utils/excel';
import { formatDateString, isReviewerOfDimensionList } from '../../utils';
import styles from './DimensionList.style';

const actionTypes = {
  deleted: 'Removed',
  added: 'Added',
};

const DimensionList = () => {
  const dispatch = useDispatch();
  const { path, url } = useRouteMatch();
  const { pathname } = useLocation();
  const history = useHistory();
  const { message } = useStatementRejection();
  const [selectedKey, setSelectedKey] = useState('overview');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditDialogVisible, setIsEditDialogVisible] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const { listId } = useParams();
  const dimensionList = useSelector(state =>
    get(state, `entities.dimensionLists.${listId}`, { metadata: {} }),
  );
  const listChangelog = useSelector(state => get(state, `entities.listChangelog.${listId}`, {}));

  // If any associated contract is paused or list is pending edit approval, pause transactions
  let transactionsPaused;
  if (dimensionList.associatedContractDetails)
    transactionsPaused = dimensionList.associatedContractDetails.some(
      contract => contract.approvalStatus === 'Paused',
    );
  if (!transactionsPaused) transactionsPaused = ['Pending'].includes(dimensionList.status);

  const pendingResubmission = dimensionList.status === 'Rejected';

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await dispatch(fetchDimensionListOverview(listId, { useCache: false }));
      await dispatch(fetchDimensionListChangelog(listId));
      setIsLoading(false);
    };

    loadData();
  }, [dispatch, listId]);

  useEffect(() => {
    if (pathname.includes('/details')) {
      setSelectedKey('details');
    } else {
      setSelectedKey('overview');
    }
  }, [pathname]);

  const onReject = () => {
    setIsApproved(false);
    setIsEditDialogVisible(true);
  };

  const onApprove = () => {
    setIsApproved(true);
    setIsEditDialogVisible(true);
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    let response;
    if (isApproved) {
      response = await dispatch(approveDimensionListEdits(listId, dimensionList));
    } else {
      response = await dispatch(
        rejectDimensionListEdits(listId, { ...dimensionList, rejectionMessage: message }),
      );
    }
    setIsSubmitting(false);
    if (response.error) {
      dispatch(showFlashMessage(response.message, { type: 'error' }));
    } else {
      const flashMessage = `Edits successfully ${isApproved ? 'approved' : 'rejected'}`;
      dispatch(showFlashMessage(flashMessage, { type: 'success' }));
    }
    setIsEditDialogVisible(false);
    history.push(`/dimensionLists`);
  };

  // returns items for cell in excel
  const itemsForCell = changedItems => {
    const itemsArray = [];
    const sortedItems = orderBy(changedItems, 'metadata.revisionApprovalDate', 'desc');
    sortedItems.forEach(changedItem => {
      const dataRevise = changedItem?.metadata;
      changedItem?.items?.forEach(eachItem => {
        if (eachItem.isList) {
          eachItem?.listItems?.forEach(listItem => {
            const listObj = {};
            listObj.attributeType = 'List';
            listObj.nestedListName = eachItem.name || '';
            listObj.itemName = listItem.name || '';
            listObj.action = dataRevise?.action ? actionTypes[dataRevise?.action] : '';
            listObj.approver = dataRevise?.approver?.displayName;
            listObj.approvalDate = dataRevise?.revisionApprovalDate
              ? formatDateString(dataRevise.revisionApprovalDate, 'EEEE, d MMMM yyyy, hh:mma', true)
              : '';
            listObj.justification = dataRevise?.revisionJustification || '';
            itemsArray.push(listObj);
          });
        } else {
          const itemObj = {};
          itemObj.attributeType = 'Item';
          itemObj.nestedListName = changedItem.name || '';
          itemObj.itemName = eachItem.name || '';
          itemObj.action = dataRevise?.action ? actionTypes[dataRevise?.action] : '';
          itemObj.approver = dataRevise?.approver?.displayName;
          itemObj.approvalDate = dataRevise?.revisionApprovalDate
            ? formatDateString(dataRevise.revisionApprovalDate, 'EEEE, d MMMM yyyy, hh:mma', true)
            : '';
          itemObj.justification = dataRevise?.revisionJustification || '';
          itemsArray.push(itemObj);
        }
      });
    });
    return itemsArray;
  };

  const exportToExcel = () => {
    const columnsHeader = [
      'Attribute Type',
      'Nested Lists Name',
      'Item Name',
      'Revision Action',
      'Approver',
      'Date of Revision Approval',
      'Revision Justification',
    ];
    const sheetHeaders = [
      {
        cell: 'A1',
        value: 'List Name:',
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'right' },
      },
      {
        cell: 'B1',
        value: listChangelog?.listName,
        font: { size: 16 },
        alignment: { horizontal: 'left' },
      },
      {
        cell: 'A2',
        value: 'List Type:',
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'right' },
      },
      {
        cell: 'B2',
        value: listChangelog?.listType ? startCase(listChangelog.listType) : '',
        font: { size: 16 },
        alignment: { horizontal: 'left' },
      },
      {
        cell: 'A3',
        value: 'Most Recent Revision Date:',
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'right' },
      },
      {
        cell: 'B3',
        value: formatDateString(listChangelog?.latestRevisionDate, 'EEEE, d MMMM yyyy'),
        font: { size: 16 },
        alignment: { horizontal: 'left' },
      },
      {
        cell: 'A4',
        value: 'Revision Requester:',
        font: { bold: true, size: 16 },
        alignment: { horizontal: 'right' },
      },
      {
        cell: 'B4',
        value: listChangelog?.revisionRequestor?.displayName,
        font: { size: 16 },
        alignment: { horizontal: 'left' },
      },
    ];
    saveExcel(columnsHeader, itemsForCell(listChangelog?.changedItems || []), sheetHeaders, {
      fileName: `${listChangelog?.listName} Revision Summary`,
      workSheetName: 'Dimension List Revision Summary',
      isRevisionSummary: true,
    });
  };

  const customRenderer = link => {
    if (!link) {
      return null;
    }

    return (
      <span className={styles.exportToExcel}>
        <FontIcon
          iconName={link.itemIcon}
          arianame={link.itemIcon}
          className={styles.bulletedList}
        />
        {link.headerText}
      </span>
    );
  };

  if (isLoading && !dimensionList?.listName) {
    return <Spinner size={SpinnerSize.medium} className={styles.loadingSpinner} />;
  }

  return (
    <>
      <PageTitle>{dimensionList.listName}</PageTitle>

      <Main>
        {transactionsPaused && (
          <MessageBar
            isMultiline={false}
            messageBarType={MessageBarType.error}
            messageBarIconProps={{ iconName: 'Info' }}
            className={styles.transactionsPaused}
          >
            {dimensionList.status !== 'Pending' && !pendingResubmission ? (
              <>
                Transaction Processing Resumed:{' '}
                <span className={styles.pendingApprovalText}>
                  {`Associated contract statuses will be changed to 'Active' once paused transactions
                  have been fully processed.`}
                </span>
              </>
            ) : (
              <>
                Transaction Processing Paused:{' '}
                <span className={styles.pendingApprovalText}>
                  Associated contract(s) are pending approval of list edit
                </span>
              </>
            )}
          </MessageBar>
        )}
        <div className={styles.actionRequiredWrapper}>
          <DimensionListActionRequired listId={listId} />
        </div>
        <Pivot
          className={!isEmpty(listChangelog) ? clsx(styles.tabs, styles.tabLink) : styles.tabs}
          aria-label="Dimension List sections"
          linkSize={PivotLinkSize.large}
          selectedKey={selectedKey}
          onLinkClick={item => {
            if (item.props.itemKey === 'exportToExcel') {
              exportToExcel();
            } else {
              history.push(item.props.href);
            }
          }}
        >
          <PivotItem headerText="Overview" itemKey="overview" href={url} />
          <PivotItem headerText="Details" itemKey="details" href={`${url}/details`} />
          {!isEmpty(listChangelog) && (
            <PivotItem
              itemKey="exportToExcel"
              headerText="Download Revision Summary"
              onRenderItemLink={customRenderer}
              itemIcon="DownloadDocument"
            />
          )}
        </Pivot>

        <Switch>
          <Route exact path={path} component={DimensionListOverview} />
          <Route exact path={`${path}/details`} component={DimensionListDetails} />
        </Switch>

        {/* Only show Approve/Reject buttons if list is not currently in a revision flow and user is assigned reviewer */}
        {transactionsPaused && !pendingResubmission && isReviewerOfDimensionList(dimensionList) && (
          <div className={styles.buttonContainer}>
            <DefaultButton onClick={onReject} disabled={isSubmitting}>
              Reject
            </DefaultButton>
            <PrimaryButton type="submit" onClick={onApprove} disabled={isSubmitting}>
              Approve
            </PrimaryButton>
          </div>
        )}

        <DimensionListReviewerDialog
          onDismiss={() => setIsEditDialogVisible(false)}
          onSubmit={onSubmit}
          isSubmitting={isSubmitting}
          hidden={!isEditDialogVisible}
          approved={isApproved}
          dialogContentProps={{
            type: DialogType.normal,
            title: `${isApproved ? 'Approve' : 'Reject'} edits?`,
            closeButtonAriaLabel: 'Close',
          }}
        />
      </Main>
    </>
  );
};

export default DimensionList;
